import React, { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TokenService } from '../../../../services/authentication/token.service';
import { FileUpload, UploadManuallyStoppedError } from '../../../../services/network/file-upload';
import { WebApi } from '../../../../services/web-api';
import { isDefined } from '../../../../utils/variables';
import { useCoordinateSystemsAndGeoidsState } from '../../../hooks/use-coordinate-systems-and-geoids-state';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { Button } from '../../button/button';
import { CheckBox } from '../../check-box/check-box';
import { Dialog } from '../../dialog/dialog';
import { DropdownSuggestion } from '../../dropdown-suggestion/dropdown-suggesion';
import { FileSelectionButton } from '../../file-selection-button/file-selection-button';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { ProgressBar } from '../../progress-bar/progress-bar';
import { Stack } from '../../stack/stack';
import { TooltipInfoIcon } from '../../tooltip-info-icon/tooltip-info-icon';
import { ManualUploadItem, ManualUploadParams } from './skyview-data';

interface Props {
  accept: string;
  title: string;
  showZUpCheckBox?: boolean;
  showCopcCheckBox?: boolean;
  showCoordinateSystem?: boolean;
  showGeoids?: boolean;
  onClose: (item?: ManualUploadItem) => void;
  onUploaded: (item: ManualUploadParams) => Promise<ManualUploadItem>;
}

enum Status {
  Idle,
  Pending,
  Error,
}

type ApiRequestPath = 'body.uploadId';

const NewUploadDialog = (props: Props) => {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState(Status.Idle);
  const [progress, setProgress] = React.useState(0);
  const [file, setFile] = React.useState<File>();
  const [zUp, setZUp] = React.useState<boolean>(false);
  const [copc, setCopc] = React.useState<boolean>(false);
  const { handleError, clearErrors, buildErrorList, includeErrorPaths } =
    useErrorHandling<ApiRequestPath>();
  const upload = React.useRef<FileUpload>();

  const {
    selectedCoordinateSystem,
    setSelectedCoordinateSystem,
    selectedGeoid,
    setSelectedGeoid,
    coordinateSystems,
    geoids,
    isLoading,
    getGeoidIdFromItem,
  } = useCoordinateSystemsAndGeoidsState();

  const coordinateSystemCompatibleFormats = useMemo(
    () =>
      props.accept
        .split(',')
        .filter((x) => x !== '.zip')
        .join(', '),
    [props.accept],
  );

  const onFileAdded = useCallback(
    async (params: {
      file: File;
      coordinateSystemId?: string;
      isZUp?: boolean;
      isCopc?: boolean;
      geoidId?: string;
    }) => {
      try {
        setStatus(Status.Pending);
        clearErrors();
        setFile(params.file);

        upload.current = new FileUpload({
          apiBaseUrl: WebApi.getUrl(),
          authToken: new TokenService().get(),
          file: params.file,
          concurrency: 3,
        });

        upload.current.onProgress((bytes) => setProgress(bytes / params.file.size));
        const result = await upload.current.start();

        const uploadId = result.id;
        const newItem = await props.onUploaded({
          filename: params.file.name,
          uploadId: uploadId,
          isZUp: params.isZUp,
          isCopc: params.isCopc,
          coordinateSystemId: params.coordinateSystemId,
          geoidId: params.geoidId,
        });

        props.onClose(newItem);
      } catch (err: unknown) {
        setStatus(Status.Error);
        if (err instanceof UploadManuallyStoppedError) {
          return;
        }

        handleError(err);
      } finally {
        setProgress(0);
      }
    },
    [clearErrors, handleError, props],
  );

  return (
    <>
      <Dialog
        closeIcon={status !== Status.Pending}
        closeOnDimmerClick={status !== Status.Pending}
        useGoogleTranslate={true}
        width={500}
        onClose={props.onClose}
      >
        {{
          header: t('manualUploads.uploadDialog.header', {
            ns: 'components',
            title: props.title,
          }),
          content: (
            <Stack direction="column" spacing={1}>
              {status !== Status.Pending && (
                <InfoBox color="yellow">
                  {props.showCoordinateSystem ? (
                    <Text topMargin={true}>
                      {t('manualUploads.fileCoordinateSystem', {
                        ns: 'components',
                        formats: coordinateSystemCompatibleFormats,
                      })}
                    </Text>
                  ) : (
                    <Text topMargin={false}>
                      {t('manualUploads.uploadDialog.infoDefault', { ns: 'components' })}
                    </Text>
                  )}
                </InfoBox>
              )}

              {props.showCoordinateSystem && (
                <LabelledContainer
                  text={t('projectSettings.projection.title', { ns: 'components' })}
                >
                  <DropdownSuggestion
                    disabled={
                      status === Status.Pending || isLoading || !isDefined(selectedCoordinateSystem)
                    }
                    placeholder={
                      isLoading ? t('loading', { ns: 'common' }) : selectedCoordinateSystem?.name
                    }
                    portalToBody={true}
                    suggestions={coordinateSystems}
                    value={selectedCoordinateSystem?.id}
                    width="100%"
                    onSuggestionSelected={(suggestion) => setSelectedCoordinateSystem(suggestion)}
                  />
                </LabelledContainer>
              )}
              {props.showGeoids && (
                <LabelledContainer
                  text={t('projectSettings.projection.geoidModel', { ns: 'components' })}
                >
                  <DropdownSuggestion
                    disabled={status === Status.Pending || isLoading || !isDefined(selectedGeoid)}
                    placeholder={isLoading ? t('loading', { ns: 'common' }) : selectedGeoid?.name}
                    portalToBody={true}
                    suggestions={geoids}
                    value={selectedGeoid?.id}
                    width="100%"
                    onSuggestionSelected={(suggestion) => setSelectedGeoid(suggestion)}
                  />
                </LabelledContainer>
              )}

              {props.showZUpCheckBox && (
                <Stack direction="row" spacing={0.5}>
                  <CheckBox
                    checked={zUp}
                    disabled={status === Status.Pending}
                    onChange={() => setZUp(!zUp)}
                  >
                    {t('manualUploads.uploadDialog.zAxisLabel', { ns: 'components' })}
                  </CheckBox>
                  <TooltipInfoIcon
                    content={
                      <div>
                        {t('manualUploads.uploadDialog.zAxisTooltipBody', {
                          ns: 'components',
                        })
                          .split('\n\n')
                          .map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                          ))}
                      </div>
                    }
                    placement="top"
                    title={t('manualUploads.uploadDialog.zAxisTooltipTitle', { ns: 'components' })}
                    useGoogleTranslate={true}
                  />
                </Stack>
              )}

              {props.showCopcCheckBox && (
                <Stack direction="row" spacing={0.5}>
                  <CheckBox
                    checked={copc}
                    disabled={status === Status.Pending}
                    onChange={() => setCopc(!copc)}
                  >
                    {t('manualUploads.uploadDialog.copcLabel', { ns: 'components' })}
                  </CheckBox>
                  <TooltipInfoIcon
                    content={t('manualUploads.uploadDialog.copcTooltipBody', { ns: 'components' })}
                    placement="top"
                    title={t('manualUploads.uploadDialog.copcTooltipTitle', { ns: 'components' })}
                    useGoogleTranslate={true}
                  />
                </Stack>
              )}

              {status === Status.Pending && (
                <Stack direction="column" spacing={1}>
                  <span>
                    <Trans
                      components={{ bold: <strong /> }}
                      i18nKey="manualUploads.uploadDialog.uploading"
                      ns="components"
                      values={{ fileName: file!.name }}
                    />
                  </span>
                  <ProgressBar
                    height={'2em'}
                    max={1}
                    maxPercentage={99}
                    min={0}
                    value={progress}
                    width={'100%'}
                  />
                </Stack>
              )}

              {buildErrorList({
                filter: includeErrorPaths(['body.uploadId']),
                defaultMessage: t('manualUploads.uploadDialog.error', { ns: 'components' }),
              })}
            </Stack>
          ),
          footer: {
            right: (
              <>
                <FileSelectionButton
                  accept={props.accept}
                  color="primary"
                  disabled={status === Status.Pending}
                  leftIcon={{ icon: ['fad', 'upload'] }}
                  multiple={false}
                  text={t('manualUploads.uploadDialog.chooseFile', { ns: 'components' })}
                  variant="contained"
                  onFilesSelected={async (files) => {
                    if (files.length > 0) {
                      await onFileAdded({
                        file: files[0],
                        coordinateSystemId: selectedCoordinateSystem?.id,
                        geoidId: getGeoidIdFromItem(selectedGeoid),
                        isZUp: zUp,
                        isCopc: copc,
                      });
                    }
                  }}
                />

                <Button
                  variant="text"
                  onClick={() => {
                    upload.current?.stop();
                    props.onClose();
                  }}
                >
                  {t('manualUploads.uploadDialog.cancel', { ns: 'components' })}
                </Button>
              </>
            ),
          },
        }}
      </Dialog>
    </>
  );
};

const Text = styled.span<{ topMargin?: boolean; bottomMargin?: boolean }>`
  display: block;

  strong {
    font-weight: 600;
  }

  margin-top: ${(props) => (props.topMargin ? '0.5em' : undefined)};
  margin-bottom: ${(props) => (props.bottomMargin ? '0.5em' : undefined)};
`;

export { NewUploadDialog };
